import { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AssistantChatMessage, ChatSuggestedEvent } from '../../../types';
import { ChatState } from '../types';
import { TEMPORAL_EVENT_PREFIX } from '../../../helpers';
import { safeLocalStorage } from '../../../../utils/safeLocalStorage';

const CHAT_THREAD_STORAGE_KEY = 'lifestack_assistant_chat_thread';

interface PersistedAssistantChatThread {
  messages: AssistantChatMessage[];
  chatSessionId: string;
}

const readPersistedThread = (): PersistedAssistantChatThread | null => {
  const raw = safeLocalStorage.getItem(CHAT_THREAD_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PersistedAssistantChatThread;
    if (!Array.isArray(parsed?.messages) || typeof parsed.chatSessionId !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const useChatState = () => {
  const [state, setState] = useState<ChatState>(() => {
    const thread = readPersistedThread();
    return {
      messages: thread?.messages ?? [],
      isUserTurn: true,
      loading: false,
      loadingStatusText: [],
      temporalSuggestedEvents: [],
      chatSessionId: thread?.chatSessionId ?? uuidv4(),
      targetEventOnCalendar: null,
      currentSessionType: null,
      userContext: null,
      isSchedulerMode: false,
      schedulerScope: 'day',
      abortControllers: [],
      activePlanContext: null,
    };
  });

  useEffect(() => {
    if (!state.messages.length || !state.chatSessionId) {
      safeLocalStorage.removeItem(CHAT_THREAD_STORAGE_KEY);
      return;
    }
    const thread: PersistedAssistantChatThread = {
      messages: state.messages,
      chatSessionId: state.chatSessionId,
    };
    safeLocalStorage.setItem(CHAT_THREAD_STORAGE_KEY, JSON.stringify(thread));
  }, [state.chatSessionId, state.messages]);

  // Bumped when the thread is cleared. Message writes capture the epoch at
  // call time and no-op if New chat has since invalidated that turn, so a
  // late question card cannot land on the fresh thread.
  const chatEpochRef = useRef(0);

  const updateState = useCallback((updates: Partial<ChatState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const addMessage = useCallback((message: any) => {
    const epoch = chatEpochRef.current;
    setState((prev) => {
      if (chatEpochRef.current !== epoch) {
        return prev;
      }
      return {
        ...prev,
        messages: [...prev.messages, message],
      };
    });
  }, []);

  const addMessages = useCallback((newMessages: any[]) => {
    const epoch = chatEpochRef.current;
    setState((prev) => {
      if (chatEpochRef.current !== epoch) {
        return prev;
      }
      return {
        ...prev,
        messages: [...prev.messages, ...newMessages],
      };
    });
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<AssistantChatMessage>) => {
    const epoch = chatEpochRef.current;
    setState((prev) => {
      if (chatEpochRef.current !== epoch) {
        return prev;
      }
      return {
        ...prev,
        messages: prev.messages.map((message) =>
          message.messageId === messageId ? { ...message, ...updates } : message
        ),
      };
    });
  }, []);

  const removeMessages = useCallback((shouldRemove: (message: AssistantChatMessage) => boolean) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.filter((message) => !shouldRemove(message)),
    }));
  }, []);

  const insertMessageBeforeId = useCallback((message: AssistantChatMessage, beforeMessageId: string) => {
    const epoch = chatEpochRef.current;
    setState((prev) => {
      if (chatEpochRef.current !== epoch) {
        return prev;
      }
      const index = prev.messages.findIndex((m) => m.messageId === beforeMessageId);
      if (index === -1) {
        return { ...prev, messages: [...prev.messages, message] };
      }
      const messages = [...prev.messages];
      messages.splice(index, 0, message);
      return { ...prev, messages };
    });
  }, []);

  const clearMessages = useCallback(() => {
    chatEpochRef.current += 1;
    setState((prev) => ({
      ...prev,
      messages: [],
      isUserTurn: true,
      loading: false,
      loadingStatusText: [],
      isSchedulerMode: false,
      schedulerScope: 'day',
      temporalSuggestedEvents: [],
      chatSessionId: uuidv4(),
      targetEventOnCalendar: null,
      currentSessionType: null,
      recommendationState: undefined,
      activePlanContext: null,
    }));
  }, []);

  const addAbortController = useCallback((controller: AbortController) => {
    setState((prev) => ({
      ...prev,
      abortControllers: [...prev.abortControllers, controller],
    }));
  }, []);

  const clearAbortControllers = useCallback(() => {
    setState((prev) => ({
      ...prev,
      abortControllers: [],
    }));
  }, []);

  const removeTemporalSuggestedEvent = useCallback((eventId: string) => {
    setState((prev) => ({
      ...prev,
      temporalSuggestedEvents: prev.temporalSuggestedEvents.filter(
        (event) => event.eventId !== eventId
      ),
    }));
  }, []);

  const setTemporalSuggestedEvents = useCallback(
    (
      eventsOrUpdater:
        | ChatSuggestedEvent[]
        | ((prevEvents: ChatSuggestedEvent[]) => ChatSuggestedEvent[])
    ) => {
      setState((prev) => ({
        ...prev,
        temporalSuggestedEvents:
          typeof eventsOrUpdater === 'function'
            ? eventsOrUpdater(prev.temporalSuggestedEvents)
            : eventsOrUpdater,
      }));
    },
    []
  );

  return {
    state,
    updateState,
    addMessage,
    addMessages,
    insertMessageBeforeId,
    removeMessages,
    updateMessage,
    clearMessages,
    addAbortController,
    clearAbortControllers,
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
  };
};
